import { createContext, useContext, useEffect } from 'react';
import SideNavigation, { SideNavigationProps } from '@cloudscape-design/components/side-navigation';
import BreadcrumbGroup, {
  BreadcrumbGroupProps,
} from '@cloudscape-design/components/breadcrumb-group';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useDistributorAuth, redirectToManage } from '../../auth/useDistributorAuth';

export type NavigationContextType = {
  navigationOpen: boolean;
  setNavigationOpen: (value: boolean) => void;
  navigationSize: number;
};

export const NavigationContext = createContext<NavigationContextType>({
  navigationOpen: true,
  navigationSize: 200,
  setNavigationOpen: (_value: boolean) => {
    return;
  },
});

const HOME = { text: 'Real Time Feedback', href: '/' };

export function Breadcrumbs({ items }: { items: BreadcrumbGroupProps.Item[] }) {
  const navigate = useNavigate();

  return (
    <BreadcrumbGroup
      items={[HOME, ...items]}
      onClick={(event) => {
        if (event.detail.external) {
          return;
        }
        event.preventDefault();
        navigate({ to: event.detail.href });
      }}
    />
  );
}

export function LocalNavigation() {
  const navigate = useNavigate();
  const location = useLocation();

  const sideItems: SideNavigationProps.Item[] = [];

  const reviewItems: SideNavigationProps.Item[] = [];

  reviewItems.push({ type: 'link', text: 'Places', href: '/places' });
  reviewItems.push({ type: 'link', text: 'Contacts', href: '/contacts' });
  reviewItems.push({ type: 'link', text: 'Lists', href: '/lists' });
  if (reviewItems.length > 0) {
    if (sideItems.length > 0) {
      sideItems.push({ type: 'divider' });
    }
    sideItems.push({
      type: 'section',
      text: 'Reviews',
      items: reviewItems,
    });
  }

  // const pricingItems: SideNavigationProps.Item[] = [];

  // pricingItems.push({ type: 'link', text: 'Quotes', href: '/quotes' });
  // if (pricingItems.length > 0) {
  //   if (sideItems.length > 0) {
  //     sideItems.push({ type: 'divider' });
  //   }
  //   sideItems.push({
  //     type: 'section',
  //     text: 'Sales',
  //     items: pricingItems,
  //   });
  // }

  // const salesItems: SideNavigationProps.Item[] = [];

  // salesItems.push({ type: 'link', text: 'Bills of Sale', href: '/bos' });
  // if (salesItems.length > 0) {
  //   if (sideItems.length > 0) {
  //     sideItems.push({ type: 'divider' });
  //   }
  //   sideItems.push({
  //     type: 'section',
  //     text: 'Business',
  //     items: salesItems,
  //   });
  // }

  return (
    <SideNavigation
      activeHref={location.pathname}
      className="side-navigation"
      items={sideItems}
      onFollow={(event) => {
        if (event.detail.external) {
          return;
        }
        event.preventDefault();
        navigate({ to: event.detail.href });
      }}
    />
  );
}

export function DistributorTopNavigation(props: {
  email?: string;
  onLogout: () => void;
}) {
  return (
    <TopNavigation
      className="top-navigation"
      identity={{
        title: 'Reputation Scorecards',
        href: 'https://manage.realtimefeedback.com',
        logo: {
          src: 'https://cdn.aiescape.io/realtimefeedback/realtime-feedback-footer-logo.png',
          alt: 'Real Time Feedback Logo',
        },
      }}
      utilities={[
        {
          type: 'menu-dropdown',
          text: props.email ?? 'Distributor',
          iconName: 'user-profile',
          onItemClick: (event) => {
            if (event.detail.id === 'logout') {
              props.onLogout();
            }
          },
          items: [
            {
              id: 'logout',
              itemType: 'action',
              text: 'Logout',
              iconName: 'redo',
            },
          ],
        },
      ]}
    />
  );
}

export function LocalTopNavigation() {
  const navigate = useNavigate();
  const { user, logout } = useDistributorAuth();

  return (
    <TopNavigation
      className="top-navigation"
      identity={{
        title: 'AI Analytics',
        href: 'https://ai.realtimefeedback.com/scorecard',
        logo: {
          src: 'https://cdn.aiescape.io/realtimefeedback/realtime-feedback-footer-logo.png',
          alt: 'Real Time Feedback Logo',
        },
      }}
      utilities={
        user
          ? [
              {
                type: 'menu-dropdown',
                text: user.email,
                iconName: 'user-profile',
                onItemClick: async (event) => {
                  if (event.detail.external) {
                    return;
                  }
                  event.preventDefault();

                  const href = event.detail.href;

                  if (href) {
                    navigate({ to: href });
                  }

                  if (event.detail.id === 'logout') {
                    logout();
                  }
                },
                items: [
                  {
                    id: 'logout',
                    itemType: 'action',
                    text: 'Logout',
                    iconName: 'redo',
                  },
                ],
              },
            ]
          : [
              {
                type: 'button',
                text: 'Login',
                onClick: (event) => {
                  event.preventDefault();
                  redirectToManage();
                },
              },
            ]
      }
    />
  );
}
